/**
 * Pure helpers for deriving a guest's access state from a scan. A scan governs
 * access until it expires or is revoked; these functions are the single source
 * of truth for that derivation so every surface agrees.
 */

export type AccessState = 'active' | 'expired' | 'revoked';

interface ScanLike {
  expiresAt: string | null;
  revokedAt?: string | null;
}

/**
 * Whether a scan's expiration has passed. A null `expiresAt` never expires.
 *
 * @param expiresAt - ISO date string or null.
 * @param now - Reference time (injected for deterministic tests).
 * @returns True when `expiresAt` is in the past.
 */
export function isScanExpired(expiresAt: string | null, now: Date = new Date()): boolean {
  if (!expiresAt) return false;
  return new Date(expiresAt) < now;
}

/**
 * Derive the access state of a single scan.
 *
 * @param scan - The scan's expiry/revocation fields.
 * @param now - Reference time (injected for deterministic tests).
 * @returns `'revoked'` if revoked, else `'expired'` if past expiry, else `'active'`.
 */
export function getScanAccessState(scan: ScanLike, now: Date = new Date()): AccessState {
  if (scan.revokedAt) return 'revoked';
  if (isScanExpired(scan.expiresAt, now)) return 'expired';
  return 'active';
}
